import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import csrfToken from '../../shared/adminCsrf';

// Whose slideshows: a lesson's (lesson guides) or a unit's (unit slideshows). The legacy admin
// actions take the unit as `unit_id` and the lesson as the nested route or `lesson_id`.
export interface SlideShowParent {
  id: number;
  type: 'Lesson' | 'Unit';
}

const parentParams = (parent: SlideShowParent) => (
  parent.type === 'Unit' ? { unit_id: parent.id } : { lesson_id: parent.id }
);

// A TCI slideshow of a lesson, as SlideShowSerializer sends it with the page's extra attributes.
export interface LessonGuide {
  archived: boolean;
  has_custom_slides: boolean;
  id: number;
  position: number;
  presentation_guide_updated_at: number | null;
  presentation_guide_url: string | null;
  question_groups_count: number;
  questions_on_slides_count: number;
  slides_count: number;
  title: string | null;
  use_new_builder: boolean;
  visible: boolean;
}

const EXTRA_ATTRIBUTES = [
  'visible', 'archived', 'use_new_builder', 'position', 'slides_count',
  'question_groups_count', 'questions_on_slides_count', 'has_custom_slides',
  'presentation_guide_url', 'presentation_guide_updated_at',
];

export const lessonGuidesKey = (parent: SlideShowParent) => [QueryKeys.LessonGuides, parent.type, parent.id];

export const useLessonGuides = (parent: SlideShowParent) => useQuery({
  queryFn: async () => {
    const scope = parent.type === 'Unit' ? { for_unit: parent.id } : { for_lesson: parent.id };
    const { data } = await Axios.get<{ data: LessonGuide[] }>(
      Routes.plato_api_slide_shows_path({ extra_attributes: EXTRA_ATTRIBUTES, tci: true, ...scope }),
    );
    return [...data.data].sort((a, b) => a.position - b.position);
  },
  queryKey: lessonGuidesKey(parent),
});

const csrf = () => ({ headers: { 'X-CSRF-Token': csrfToken() } });

// Every write goes through the legacy admin actions (they own the slideshow rules), asking for
// JSON; the list is patched or refetched afterwards.
export const useCreateLessonGuide = (programId: number, parent: SlideShowParent) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (useNewBuilder: boolean) => {
      const options = { format: 'json', program_id: programId, use_new_builder: useNewBuilder };
      const path = parent.type === 'Unit'
        ? Routes.admin_unit_slide_shows_path(parent.id, options)
        : Routes.admin_lesson_slide_shows_path(parent.id, options);

      return Axios.post<{ data: { id: number } }>(path, {}, csrf()).then(res => res.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lessonGuidesKey(parent) });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Lessons, programId] });
    },
  });
};

const patchGuide = (
  queryClient: ReturnType<typeof useQueryClient>, parent: SlideShowParent, id: number, patch: Partial<LessonGuide>,
) => {
  queryClient.setQueryData<LessonGuide[]>(
    lessonGuidesKey(parent),
    guides => (guides ?? []).map(guide => (guide.id === id ? { ...guide, ...patch } : guide)),
  );
};

export const useToggleGuideVisibility = (programId: number, parent: SlideShowParent) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (guide: LessonGuide) => Axios
      .get(Routes.toggle_visibility_admin_slide_show_path(guide.id, {
        format: 'json', index: true, program_id: programId, ...parentParams(parent),
      }))
      .then(() => guide),
    onSuccess: guide => patchGuide(queryClient, parent, guide.id, { visible: !guide.visible }),
  });
};

export const useDeleteLessonGuide = (programId: number, parent: SlideShowParent) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => Axios
      .delete(
        Routes.admin_slide_show_path(id, { format: 'json', program_id: programId, ...parentParams(parent) }),
        csrf(),
      )
      .then(() => id),
    onSuccess: (id) => {
      queryClient.setQueryData<LessonGuide[]>(
        lessonGuidesKey(parent),
        guides => (guides ?? []).filter(guide => guide.id !== id),
      );
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Lessons, programId] });
    },
  });
};

// Optimistic, like the lessons table: the cached order changes at once and the ids post. The
// reorder route nests under the program; the action renumbers whatever ids it gets.
export const useReorderLessonGuides = (programId: number, parent: SlideShowParent) => {
  const queryClient = useQueryClient();

  return (orderedRows: LessonGuide[]) => {
    queryClient.setQueryData<LessonGuide[]>(lessonGuidesKey(parent), orderedRows);

    return Axios
      .post(
        Routes.reorder_admin_program_slide_shows_path(programId),
        { slideshow: orderedRows.map(row => row.id) },
        csrf(),
      )
      .catch((error) => {
        queryClient.invalidateQueries({ queryKey: lessonGuidesKey(parent) });
        throw error;
      });
  };
};

// Classic-builder copies clone inline (lessons only, as the legacy page offered); new-builder
// copies run CopySlideShowWorker and hand back a job id, which the row polls until the copy lands.
export const useCopyLessonGuide = (programId: number, parent: SlideShowParent) => useMutation({
  mutationFn: (guide: LessonGuide) => {
    if (guide.use_new_builder) {
      return Axios
        .put<{ job_id: string }>(
        Routes.clone_new_slide_show_admin_slide_show_path(guide.id, { program_id: programId }),
        { parent_id: parent.id, parent_type: parent.type, title_en: `${guide.title ?? 'Lesson Guide'} (Copy)` },
        csrf(),
      )
        .then(res => ({ jobId: res.data.job_id }));
    }

    return Axios
      .post(
        Routes.clone_admin_slide_show_path(guide.id, { format: 'json', lesson_id: parent.id, program_id: programId }),
        {},
        csrf(),
      )
      .then(() => ({ jobId: null }));
  },
});

// Kicks off PresentationGuideWorker; the row then polls pdf_updated with the previous timestamp.
export const useBuildGuidePdf = (programId: number, parent: SlideShowParent) => useMutation({
  mutationFn: (id: number) => Axios
    .get<{ previous_update: number }>(
    Routes.build_pdf_admin_slide_show_path(id, { program_id: programId, ...parentParams(parent) }),
  )
    .then(res => res.data.previous_update),
});

export const usePdfUpdatedPoll = (
  parent: SlideShowParent, guideId: number, since: number | null, onReady: (url: string) => void,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    enabled: since !== null,
    onSuccess: (data) => {
      if (!data?.url) return;

      patchGuide(queryClient, parent, guideId, { presentation_guide_url: data.url });
      onReady(data.url);
    },
    queryFn: () => Axios
      .get<{ url?: string }>(Routes.pdf_updated_admin_slide_show_path(guideId, { timestamp: since ?? 0 }))
      .then(res => res.data),
    queryKey: [QueryKeys.LessonGuides, 'pdf', guideId, since],
    refetchInterval: data => (data?.url ? false : 2000),
  });
};

export const guideTitle = (guide: LessonGuide) => guide.title || '(untitled)';
